import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Page } from "@/components/Page";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const cat = params.get("cat") || "all";
  const { settings } = useSettings();
  useSeo(settings?.seo?.products);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  const filters = [{ slug: "all", name: "Todos" }, ...categories.map((c) => ({ slug: c.name, name: c.name }))];

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (cat && cat !== "all") p.category = cat;
    if (search) p.search = search;
    const t = setTimeout(() => {
      api.get("/products", { params: p }).then((r) => { setProducts(r.data); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [cat, search]);

  const setCat = (slug) => {
    if (slug === "all") setParams({});
    else setParams({ cat: slug });
  };

  return (
    <Page className="pt-32 md:pt-40 px-6 md:px-12 max-w-6xl mx-auto min-h-screen">
      <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Os nossos <span className="text-gradient">produtos</span></h1>
      <p className="text-muted-foreground mt-3 text-lg">Descobre criações 3D únicas para ti, para casa ou para oferecer.</p>

      <div className="relative mt-8 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <input
          data-testid="product-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar produtos..."
          className="w-full rounded-full border border-border bg-card pl-12 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
        />
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {filters.map((c) => (
          <button
            key={c.slug}
            data-testid={`filter-${c.slug}`}
            onClick={() => setCat(c.slug)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              cat === c.slug ? "brand-gradient text-white" : "bg-card border border-border text-foreground/70 hover:bg-muted"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-10 pb-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] rounded-[2rem] bg-muted animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <p data-testid="no-products" className="text-center text-muted-foreground py-20 text-lg">Nenhum produto encontrado. Experimenta outra pesquisa.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </Page>
  );
}
